import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Header } from "build-ts/metadata";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeToJSON } from "build-ts/common";
import { listJobArtifacts } from "services/AgentChatService";
import { populateArtifact } from "services/DataService";
import { useUser } from "context/UserContext";
import WorkflowRunsOutputList from "modules/workflow/components/WorkflowRunsOutputList";
import useRunAuthorNames from "hooks/useRunAuthorNames";

interface WorkflowRunsOutputListContainerProps {
  selectedJob: Header | undefined;
  /** When false the container skips fetching (e.g. tab is not visible). */
  isTabActive: boolean;
}

const WorkflowRunsOutputListContainer: React.FC<WorkflowRunsOutputListContainerProps> = ({
  selectedJob,
  isTabActive,
}) => {
  const { defaultTimeZone } = useUser();

  const [outputs, setOutputs] = useState<ArtifactProto[]>([]);
  const [totalOutputs, setTotalOutputs] = useState(0);
  const [isLoadingOutputs, setIsLoadingOutputs] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Keyed by workflow_run.header.id → populated (hydrated) ArtifactProto
  const [populatedRuns, setPopulatedRuns] = useState<Record<string, ArtifactProto>>({});
  // Set of run header IDs that are currently expanded
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Set of run header IDs currently being populated
  const [loadingPopulateIds, setLoadingPopulateIds] = useState<Set<string>>(new Set());
  // True only while handleShowSynthesis is populating all runs.
  const [isSynthesisLoading, setIsSynthesisLoading] = useState(false);

  const runRefs = useMemo(
    () =>
      outputs
        .filter(a => !!a.workflow_run?.header?.id && !!a.workflow_run?.source_chat_id)
        .map(a => ({ runId: a.workflow_run!.header!.id!, sourceChatId: a.workflow_run!.source_chat_id! })),
    [outputs]
  );

  const runAuthorNames = useRunAuthorNames(runRefs);

  const resetRunState = useCallback(() => {
    setOutputs([]);
    setTotalOutputs(0);
    setPopulatedRuns({});
    setExpandedIds(new Set());
    setLoadingPopulateIds(new Set());
    setIsSynthesisLoading(false);
  }, []);

  const fetchOutputs = useCallback(async (jobId: string, offset: number, append: boolean) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingOutputs(true);
      setPopulatedRuns({});
      setExpandedIds(new Set());
    }
    const { items, total } = await listJobArtifacts(artifactTypeToJSON(ArtifactType.WORKFLOW_RUN), jobId, 50, offset);
    setOutputs(prev => (append ? [...prev, ...items] : items));
    setTotalOutputs(total);
    if (append) {
      setIsLoadingMore(false);
    } else {
      setIsLoadingOutputs(false);
    }
  }, []);

  // Fetch runs whenever the selected job changes or the tab becomes active
  useEffect(() => {
    if (!selectedJob?.id || !isTabActive) return;
    fetchOutputs(selectedJob.id, 0, false);
  }, [selectedJob?.id, isTabActive, fetchOutputs]);

  // Clear state when the job changes
  useEffect(() => {
    resetRunState();
  }, [selectedJob?.id, resetRunState]);

  /** Populate a single run on expand. If already populated, just toggle expand. */
  const handleExpandRun = useCallback(
    async (artifact: ArtifactProto) => {
      const runId = artifact.workflow_run?.header?.id;
      if (!runId) return;

      // Toggle collapse
      if (expandedIds.has(runId)) {
        setExpandedIds(prev => {
          const next = new Set(prev);
          next.delete(runId);
          return next;
        });
        return;
      }

      // Already populated — just expand
      if (populatedRuns[runId]) {
        setExpandedIds(prev => new Set(prev).add(runId));
        return;
      }

      // Populate then expand
      setLoadingPopulateIds(prev => new Set(prev).add(runId));
      const result = await populateArtifact(artifact);
      setLoadingPopulateIds(prev => {
        const next = new Set(prev);
        next.delete(runId);
        return next;
      });
      if (result) {
        setPopulatedRuns(prev => ({ ...prev, [runId]: result }));
        setExpandedIds(prev => new Set(prev).add(runId));
      }
    },
    [expandedIds, populatedRuns]
  );

  /** Populate and expand all runs, or collapse all if every run is already expanded. */
  const handleShowSynthesis = useCallback(async () => {
    const allIds = outputs.map(a => a.workflow_run?.header?.id).filter((id): id is string => !!id);

    // Collapse all if every run is already expanded
    if (allIds.length > 0 && allIds.every(id => expandedIds.has(id))) {
      setExpandedIds(new Set());
      return;
    }

    const toPopulate = outputs.filter(a => {
      const runId = a.workflow_run?.header?.id;
      return runId && !populatedRuns[runId];
    });

    setIsSynthesisLoading(true);

    if (toPopulate.length > 0) {
      const ids = toPopulate.map(a => a.workflow_run!.header!.id!);
      setLoadingPopulateIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.add(id));
        return next;
      });

      const results = await Promise.allSettled(toPopulate.map(a => populateArtifact(a)));

      setLoadingPopulateIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });

      const newPopulated: Record<string, ArtifactProto> = {};
      results.forEach((result, i) => {
        if (result.status === "fulfilled" && result.value) {
          const runId = toPopulate[i].workflow_run?.header?.id;
          if (runId) newPopulated[runId] = result.value;
        }
      });
      setPopulatedRuns(prev => ({ ...prev, ...newPopulated }));
    }

    setExpandedIds(new Set(allIds));
    setIsSynthesisLoading(false);
  }, [outputs, populatedRuns, expandedIds]);

  const handleLoadMore = useCallback(() => {
    if (selectedJob?.id) {
      fetchOutputs(selectedJob.id, outputs.length, true);
    }
  }, [selectedJob?.id, outputs.length, fetchOutputs]);

  return (
    <WorkflowRunsOutputList
      outputs={outputs}
      totalOutputs={totalOutputs}
      isLoadingOutputs={isLoadingOutputs}
      isLoadingMore={isLoadingMore}
      populatedRuns={populatedRuns}
      expandedIds={expandedIds}
      loadingPopulateIds={loadingPopulateIds}
      defaultTimeZone={defaultTimeZone}
      isSynthesisLoading={isSynthesisLoading}
      onExpandRun={handleExpandRun}
      onShowSynthesis={handleShowSynthesis}
      onLoadMore={handleLoadMore}
      runAuthorNames={runAuthorNames}
    />
  );
};

export default WorkflowRunsOutputListContainer;
